import { useRouter } from "expo-router"
import React from "react"
import { useTranslation } from "react-i18next"

import { setDeviceSortDirection, setDeviceSortKey } from "@/base/async-storage"
import { DeviceSortKeys } from "@/base/Sorting"
import { objectEntries } from "@/base/utility"
import { useErrorHandler } from "@/hooks/useErrorHandler"
import { usePermissionCheckedAction } from "@/hooks/usePermissionCheckedAction"
import {
  getDeviceSortDirectionFromState,
  getDeviceSortKeyFromState,
} from "@/redux/selectors"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useRootSelector } from "@/redux/useRootSelector"
import {
  AppText,
  Box,
  Button,
  CloseIconButton,
  Menu,
  Row,
  SortListButton,
  TitleWithIcon,
} from "@/theme"

import {
  SortDirectionIconButton,
  SortListItem,
} from "./SortDirectionIconButton"

import type {
  ButtonProps,
  IconButtonStyleProps,
  MenuProps,
  RowProps,
} from "@/theme"

import { StyleSheet } from "react-native"

const styles = StyleSheet.create({
  sortMenu: { width: 250 },
})

function DeviceRosterSortMenu({
  _trigger,
  isInverted = false,
  ...props
}: Omit<MenuProps, "isOpen" | "onClose"> & {
  _trigger?: ButtonProps
  isInverted?: boolean
}): React.JSX.Element {
  const { t } = useTranslation("devices")
  const [isOpen, setIsOpen] = React.useState(false)
  const currentSortKey = useRootSelector(getDeviceSortKeyFromState)
  const currentSortDirection = useRootSelector(getDeviceSortDirectionFromState)
  const dispatch = useRootDispatch()
  const handleError = useErrorHandler()

  const titleText: string = t("deviceRosterSortMenuTitle")
  const handleClose = () => setIsOpen(false)
  const items = objectEntries(DeviceSortKeys)

  return (
    <React.Fragment>
      <SortListButton
        isInverted={isInverted}
        size={"sm"}
        onPress={() => setIsOpen(true)}
        {..._trigger}
      />
      <Menu
        {...props}
        isOpen={isOpen}
        style={styles.sortMenu}
        onClose={handleClose}
      >
        <Row justifyContent={"space-between"} mb={"$2"}>
          <AppText colorScheme={"secondary"}>{titleText}</AppText>
          <CloseIconButton onPress={handleClose} />
        </Row>
        {items.map(
          ([key, { IconComponent, text }], index): React.JSX.Element => {
            const isSelected = currentSortKey === key

            const handlePress = () => {
              if (isSelected) {
                dispatch(
                  setDeviceSortDirection(
                    /**
                     * Toggle the sort direction
                     */
                    currentSortDirection === "asc" ? "desc" : "asc",
                  ),
                )
                  .unwrap()
                  .catch((error) => {
                    handleError(error, { toastMessage: "default" })
                  })
              } else {
                dispatch(setDeviceSortKey(key))
                  .unwrap()
                  .catch((error) => {
                    handleError(error, { toastMessage: "default" })
                  })
              }
            }
            return (
              <SortListItem
                key={key}
                IconComponent={IconComponent}
                isSelected={isSelected}
                showDivider={index < items.length - 1}
                sortDirection={currentSortDirection}
                text={text}
                onPress={handlePress}
              />
            )
          },
        )}
      </Menu>
    </React.Fragment>
  )
}

function DeviceRosterSortDirectionButton({
  isInverted,
  ...props
}: IconButtonStyleProps & {
  isInverted?: boolean
}): React.JSX.Element {
  const dispatch = useRootDispatch()
  const currentValue = useRootSelector(getDeviceSortDirectionFromState)
  const handleError = useErrorHandler()

  return (
    <SortDirectionIconButton
      {...props}
      currentDirection={currentValue}
      isInverted={isInverted === true}
      variant={"text"}
      onPress={() => {
        dispatch(
          setDeviceSortDirection(currentValue === "asc" ? "desc" : "asc"),
        )
          .unwrap()
          .catch((error) => handleError(error, { toastMessage: "default" }))
      }}
    />
  )
}

export function AddDeviceLinkButton() {
  const router = useRouter()
  const withPermission = usePermissionCheckedAction()
  const { t } = useTranslation()
  return (
    <Button
      IconComponent={"Add"}
      accessibilityRole={"link"}
      id={"addDeviceLink"}
      size={"sm"}
      text={t("addDeviceButton", { ns: "addDevice" })}
      onPress={withPermission({
        callback: () => router.navigate("/devices/add-device"),
        required: "canManageDeviceConfiguration",
      })}
    />
  )
}

export function DeviceRosterListHeaderComponent({
  HelpComponent,
  itemCount,
  ...rest
}: RowProps & {
  HelpComponent?: React.JSX.Element | null | undefined
  itemCount: number
}) {
  const { t } = useTranslation("devices")
  const titleText = t("deviceRosterTitleWithItemCount", { itemCount })

  return (
    <Row {...rest}>
      <TitleWithIcon helpElement={HelpComponent} titleText={titleText} />
      <Row ml={"auto"}>
        <AddDeviceLinkButton />
        <Box ml={"$2"}>
          <DeviceRosterSortMenu />
        </Box>
        <Box ml={"$2"}>
          <DeviceRosterSortDirectionButton />
        </Box>
      </Row>
    </Row>
  )
}
